import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, PageSizeType, Table } from 'design-system-web';
import { Alert } from 'components/lds';

import { buildCoverageHoursRowActions } from './coverageHoursRowActions';
import { formatCoverageHours } from './coverageHoursRowFormat';
import { CoverageHoursEntry, CoverageHoursQueryError } from './useCoverageHours';

type Props = {
  entries: CoverageHoursEntry[];
  totalRows: number;
  loading: boolean;
  error?: CoverageHoursQueryError;
  canManage: boolean;
  page: number;
  pageSize: PageSizeType;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: PageSizeType) => void;
};

// The server hashes every grouping field into the id, including the timezone.
// A key built from the displayed fields cannot separate two entries that differ
// only by zone.
export const coverageHoursRowId = (row: CoverageHoursEntry): string => row.id;

export function CoverageHoursTable({
  entries,
  totalRows,
  loading,
  error,
  canManage,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const { t } = useTranslation();

  const columns: Column<CoverageHoursEntry>[] = useMemo(
    () => [
      {
        key: 'categoryIssue',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.categoryIssue'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursEntry) =>
          `${row.category} – ${
            row.issue ??
            t('web.admin.serviceRequest.settings.coverageHours.create.none')
          }`,
      },
      {
        key: 'team',
        header: t('web.admin.serviceRequest.settings.coverageHours.table.team'),
        renderCell: (_cell: unknown, row: CoverageHoursEntry) =>
          row.teamName ??
          t(
            'web.admin.serviceRequest.settings.coverageHours.table.teamUnavailable'
          ),
      },
      {
        key: 'coverageHours',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.coverageHours'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursEntry) =>
          formatCoverageHours(row),
      },
    ],
    [t]
  );

  const rowActions = useMemo(
    () => (canManage ? buildCoverageHoursRowActions(t) : undefined),
    [canManage, t]
  );

  if (error) {
    return (
      <Alert type="error">
        {t('web.admin.serviceRequest.settings.coverageHours.error.load')}
      </Alert>
    );
  }

  return (
    <Table<CoverageHoursEntry>
      columns={columns}
      data={entries}
      rowActions={rowActions}
      disableSorting
      isLoading={loading}
      getRowId={coverageHoursRowId}
      pagination="server"
      totalRows={totalRows}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      showColumnVisibility
      tableKey="serviceRequestSettings.coverageHoursTable"
      emptyMessage={t(
        canManage
          ? 'web.admin.serviceRequest.settings.coverageHours.empty'
          : 'web.admin.serviceRequest.settings.coverageHours.emptyReadOnly'
      )}
    />
  );
}
